import { Box, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/formatting";

type ProductPackaging = {
    packaging?: {
        "@type"?: "PackagingElement" | "PackagingComposite";
        name: string;
        externalId?: string;
        weight?: number | null;
    } | null;
    packagingReference?: {
        name: string;
        compositeId: string;
        kind: "element" | "composite";
    };
    packagingReferenceId?: number | null;
    packagingReferenceKind?: "element" | "composite" | null;
    packagingReferenceName?: string | null;
    usedWeight?: number | null;
    quantity: number | null;
};

type ProductPackagingProps = {
    productPackagings: ProductPackaging[];
};
export const ProductPackagingsSummary = (props: ProductPackagingProps) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const { productPackagings } = props;

    if (productPackagings.length === 0) {
        return (
            <Typography
                variant="h3"
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                {t("pas_d_emballage_associe_a_ce_produit")}
            </Typography>
        );
    }

    const noValue = "-";
    const getReferenceKind = (productPackaging: ProductPackaging) =>
        productPackaging.packagingReference?.kind ??
        productPackaging.packagingReferenceKind ??
        (productPackaging.packaging?.["@type"] === "PackagingElement" ? "element" : null) ??
        (productPackaging.packaging?.["@type"] === "PackagingComposite" ? "composite" : null);
    const getPackagingName = (productPackaging: ProductPackaging) =>
        productPackaging.packaging?.name ||
        productPackaging.packagingReference?.name ||
        productPackaging.packagingReferenceName ||
        noValue;
    const getPackagingIdentifier = (productPackaging: ProductPackaging) => {
        if (productPackaging.packaging?.externalId) {
            return productPackaging.packaging.externalId;
        }

        if (productPackaging.packagingReference?.compositeId) {
            return productPackaging.packagingReference.compositeId;
        }

        const referenceKind = productPackaging.packagingReferenceKind;
        const referenceId = productPackaging.packagingReferenceId;

        return (
                referenceKind !== null &&
                    referenceKind !== undefined &&
                    referenceId !== null &&
                    referenceId !== undefined
            ) ?
                `${referenceKind}-${referenceId}`
            :   noValue;
    };
    const formatWeight = (value: number) =>
        `${formatNumber(value, i18n.resolvedLanguage, { maximumFractionDigits: 3 })} g`;
    const getExpectedValue = (productPackaging: ProductPackaging) => {
        if (productPackaging.packaging?.["@type"] === "PackagingElement") {
            const weight = productPackaging.packaging.weight;
            if (weight === null || weight === undefined) {
                return noValue;
            }

            const quantity = productPackaging.quantity ?? 1;
            return quantity > 1 ? `${quantity} × ${formatWeight(weight)}` : formatWeight(weight);
        }

        const isElementReference = getReferenceKind(productPackaging) === "element";
        const value = isElementReference ? productPackaging.usedWeight : productPackaging.quantity;
        if (value === null || value === undefined) {
            return noValue;
        }

        return isElementReference ? formatWeight(value) : formatNumber(value, i18n.resolvedLanguage);
    };

    return (
        <>
            <Typography variant="h4">{t("liste_des_emballages_du_produit")}</Typography>
            <Box
                component="table"
                sx={{
                    display: "table",
                    width: "100%",
                    "& tr": {
                        display: "flex",
                    },
                    "& tr>td": {
                        flex: 1,
                        borderBottom: `1px solid ${theme.custom.grey[500]}`,
                        padding: "16px 0px",
                    },
                    "& tr>th": {
                        flex: 1,
                        borderBottom: `1px solid ${theme.custom.grey[500]}`,
                        padding: "16px 0px",
                        textAlign: "left",
                    },
                }}
            >
                <thead>
                    <tr>
                        <th style={{ minWidth: "340px" }}>
                            <Typography sx={{ color: theme.custom.grey[2000] }} variant="body">
                                {t("packagings.name")}
                            </Typography>
                        </th>
                        <th>
                            <Typography sx={{ color: theme.custom.grey[2000] }} variant="body">
                                {t("unique_identifier")}
                            </Typography>
                        </th>
                        <th>
                            <Typography sx={{ color: theme.custom.grey[2000] }} variant="body">
                                {t("packagings.form.quantity", "Quantity / Weight (g)")}
                            </Typography>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {productPackagings.map((productPackaging, index) => {
                        return (
                            <tr key={`sub-summary-${index}`}>
                                <td style={{ minWidth: "340px", width: "fit-content" }}>
                                    <Typography variant="big-bold">{getPackagingName(productPackaging)}</Typography>
                                </td>
                                <td>
                                    <Typography variant="body-medium">
                                        {getPackagingIdentifier(productPackaging)}
                                    </Typography>
                                </td>
                                <td>
                                    <Typography variant="body-medium">{getExpectedValue(productPackaging)}</Typography>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Box>
        </>
    );
};
